import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from 'react';
import { recipeCache } from '../utils/recipeCache';
import { useBrewfatherResource } from '../hooks/useBrewfatherResource';
import { useOptionalBrewery } from './BreweryContext';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import logger from '../utils/logger';

// Recipe interface based on Brewfather API structure
export interface Recipe {
  _id: string;
  _timestamp_ms?: number;
  name: string;
  batchSize: number;
  boilTime?: number;
  efficiency?: number;
  style?: {
    name: string;
    categoryNumber?: string;
    styleLetter?: string;
  };
  defaults?: {
    volume: string;
    weight: string;
    hops: string;
  };
  equipment?: {
    name: string;
  };
  brewer?: string;
  author?: string;
  nutrition?: {
    calories: {
      total: number;
      alcohol: number;
      carbs: number;
      kJ: number;
    };
    carbs: {
      total: number;
    };
  };
  yeast?: {
    steps?: Array<{
      startVol?: number;
      startGravity?: number;
      starterGramExtract?: number;
      starterGramLiquidExtract?: number;
      [key: string]: any;
    }>;
    [key: string]: any;
  };
  fermentables?: Array<{
    _id: string;
    name: string;
    amount: number;
    [key: string]: any;
  }>;
  hops?: Array<{
    _id: string;
    name: string;
    amount: number;
    alpha?: number;
    time?: number;
    use?: string;
    [key: string]: any;
  }>;
  yeasts?: Array<{
    _id?: string;
    name: string;
    amount: number;
    [key: string]: any;
  }>;
  miscs?: Array<{
    _id?: string;
    name: string;
    amount: number;
    [key: string]: any;
  }>;
}

interface RecipeContextType {
  // All recipes data
  allRecipes: Recipe[];
  // Loading states
  isLoading: boolean;
  isRefetching: boolean;
  // Error state
  error: string | null;
  // Actions
  refreshRecipes: () => Promise<void>;
  refreshRecipesInBackground: () => Promise<void>;
  // Cache info
  lastUpdated: Date | null;
  cacheExpiry: Date | null;
  // Last sync info for header display
  lastSynced: Date | null;
  // Lazy load trigger
  ensureRecipesLoaded: () => Promise<void>;
  // Clear all in-memory data
  clearAllData: () => void;
}

const RecipeContext = createContext<RecipeContextType | undefined>(undefined);

interface RecipeProviderProps {
  children: ReactNode;
  apiKey: string | null;
  breweryId?: string | null; // If set, read from Firestore instead of API
}

export const RecipeProvider: React.FC<RecipeProviderProps> = ({ children, apiKey, breweryId }) => {
  // Homebrewer path: Brewfather API + localStorage cache via the shared sync
  // layer. Disabled in brewery mode by passing a null apiKey.
  const resource = useBrewfatherResource<Recipe>(
    {
      apiKey: breweryId ? null : apiKey,
      endpoint: 'recipes',
      // complete=true is required: the default list returns only
      // _id/name/author/style/type — no batchSize/ingredients and no
      // _timestamp_ms, which silently breaks the incremental-sync diff.
      // Same pattern batches use.
      params: { complete: true },
      versionDomain: 'recipes',
    },
    recipeCache
  );

  // Brewery path: real-time listener on the brewery's server-synced recipes.
  // Refresh triggers the brewerySync Cloud Function (admin only).
  const breweryCtx = useOptionalBrewery();
  const [breweryRecipes, setBreweryRecipes] = useState<Recipe[]>([]);
  const [breweryLoading, setBreweryLoading] = useState(false);
  const [breweryError, setBreweryError] = useState<string | null>(null);
  const [brewerySynced, setBrewerySynced] = useState<Date | null>(null);

  useEffect(() => {
    if (!breweryId) return;

    logger.log('RecipeContext: Brewery mode - listening to Firestore for recipes');
    setBreweryLoading(true);

    const recipesRef = collection(db, 'breweries', breweryId, 'recipes');
    const unsubscribe = onSnapshot(
      recipesRef,
      (snapshot) => {
        const firestoreRecipes: Recipe[] = [];
        snapshot.forEach((doc) => {
          firestoreRecipes.push(doc.data() as Recipe);
        });
        firestoreRecipes.sort((a, b) => (b._timestamp_ms || 0) - (a._timestamp_ms || 0));
        setBreweryRecipes(firestoreRecipes);
        setBrewerySynced(new Date());
        setBreweryLoading(false);
        setBreweryError(null);
      },
      (error) => {
        logger.error('RecipeContext: Error listening to Firestore recipes:', error);
        setBreweryError(error.message);
        setBreweryLoading(false);
      }
    );

    return () => unsubscribe();
  }, [breweryId]);

  const noopAsync = useCallback(async () => {}, []);

  // Errors surface through syncError; swallow the rejection (see BatchContext)
  const brewerySyncRefresh = useCallback(async () => {
    if (!breweryCtx?.canSync) return;
    await breweryCtx.syncBreweryData().catch(() => null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breweryCtx?.canSync, breweryCtx?.syncBreweryData]);

  const clearAllData = useCallback(() => {
    setBreweryRecipes([]);
    setBrewerySynced(null);
    setBreweryError(null);
    resource.clearAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource.clearAllData]);

  const value: RecipeContextType = useMemo(() => ({
    allRecipes: breweryId ? breweryRecipes : resource.items,
    isLoading: breweryId ? breweryLoading : resource.isLoading,
    isRefetching: breweryId ? (breweryCtx?.isSyncing ?? false) : resource.isRefetching,
    error: breweryId ? (breweryError || breweryCtx?.syncError || null) : resource.error,
    refreshRecipes: breweryId ? brewerySyncRefresh : resource.refresh,
    refreshRecipesInBackground: breweryId ? brewerySyncRefresh : resource.refreshInBackground,
    lastUpdated: breweryId ? (breweryCtx?.lastSyncedAt ?? brewerySynced) : resource.lastUpdated,
    cacheExpiry: null,
    lastSynced: breweryId ? (breweryCtx?.lastSyncedAt ?? brewerySynced) : resource.lastSynced,
    ensureRecipesLoaded: breweryId ? noopAsync : resource.ensureLoaded,
    clearAllData,
  }), [
    breweryId, breweryRecipes, breweryLoading, breweryError, brewerySynced,
    noopAsync, clearAllData, brewerySyncRefresh,
    breweryCtx?.isSyncing, breweryCtx?.syncError, breweryCtx?.lastSyncedAt,
    resource.items, resource.isLoading, resource.isRefetching, resource.error,
    resource.refresh, resource.refreshInBackground, resource.lastUpdated,
    resource.lastSynced, resource.ensureLoaded,
  ]);

  return (
    <RecipeContext.Provider value={value}>
      {children}
    </RecipeContext.Provider>
  );
};

export const useRecipes = (): RecipeContextType => {
  const context = useContext(RecipeContext);
  if (context === undefined) {
    throw new Error('useRecipes must be used within a RecipeProvider');
  }
  return context;
};
